import styled from "@emotion/styled";
import { Button } from "react-aria-components";
import { Theme } from "@emotion/react";
import { IMDSButtonProps, MDSButtonVariant } from "./button.types";

export const StyledButton = styled(Button)<IMDSButtonProps>(
  ({ theme, variant = MDSButtonVariant.Default, isToggled }) => ({
    position: "relative",
    display: "flex",
    textWrap: "nowrap",
    flexDirection: "row",
    gap: theme.spacing["1"],
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "nowrap",
    borderRadius: "30px",
    cursor: "pointer",
    transition: "0.25s ease",
    userSelect: "none",
    /**
     * On desktop, the outer region is draggable.
     * We want to ensure this card is not draggable.
     */
    WebkitAppRegion: "no-drag",

    fontWeight: theme.fontWeights["smedium"],
    fontSize: theme.fontSizes["text-s"],
    lineHeight: theme.lineHeights["text-s"],
    padding: theme.spacing["3"],

    ...getColors({ variant, theme, isToggled }),
  })
);

const getColors = ({
  theme,
  variant,
  isToggled,
}: {
  theme: Theme;
  variant: MDSButtonVariant;
  isToggled?: boolean;
}) => {
  const baselineStyle = {
    height: "40px",
    color: theme.colors.text.primary,
    backgroundColor: theme.colors.background.button.default.default,

    "&[data-hovered]:not([data-disabled])": {
      backgroundColor: theme.colors.background.button.default.hover,
      transition: "0.2s all",
    },

    "&[data-disabled]": {
      cursor: "not-allowed",
      opacity: 0.4,
    },

    "& .mds-icon": {
      color: theme.colors.icon.primary,
      fontSize: "14px",
      height: "14px",
      width: "14px",
    },
  };

  switch (variant) {
    case MDSButtonVariant.Default:
      return baselineStyle;

    case MDSButtonVariant.Transparent:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.transparent.default,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.transparent.hover,
        },
      };

    case MDSButtonVariant.Toggle:
      return {
        ...baselineStyle,
        height: "unset",
        padding: `${theme.spacing["2"]} ${theme.spacing["3"]}`,
        color: isToggled ? theme.colors.text.emphasis : theme.colors.text.tertiary,
        backgroundColor: isToggled
          ? theme.colors.background.button.toggle.toggled
          : theme.colors.background.button.toggle.default,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: isToggled
            ? theme.colors.background.button.toggle.toggledHover
            : theme.colors.background.button.toggle.hover,
        },

        "& .mds-icon": {
          color: isToggled ? theme.colors.icon.selected : theme.colors.icon.placeholder,
        },
      };

    case MDSButtonVariant.Chrome:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.chrome.default,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.chrome.hover,
        },
      };

    case MDSButtonVariant.Subtle:
      return {
        ...baselineStyle,
        height: "unset",
        backgroundColor: theme.colors.background.button.subtle.default,
        color: theme.colors.text.secondary,
        padding: `6px ${theme.spacing["2"]};`,
        opacity: 0.8,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.subtle.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.secondary,
          fontSize: "14px",
          height: "14px",
          width: "14px",
        },
      };

    case MDSButtonVariant.Contrast:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.contrast.default,
        color: theme.colors.text.onAccent,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.contrast.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.onAccent,
        },
      };
    case MDSButtonVariant.Emphasis:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.emphasis.default,
        color: theme.colors.text.onAccent,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.emphasis.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.onAccent,
        },
      };
    case MDSButtonVariant.Danger:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.danger.default,
        color: theme.colors.text.danger,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.danger.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.danger,
        },
      };

    case MDSButtonVariant.DangerFilled:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.dangerFilled.default,
        color: theme.colors.text.danger,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.dangerFilled.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.danger,
        },
      };

    case MDSButtonVariant.EmphasisLight:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.blueprint[100],
        color: theme.colors.text.emphasis,
        padding: `${theme.spacing["2"]} ${theme.spacing["3"]}`,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.emphasisLight.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.emphasis,
        },
      };
    case MDSButtonVariant.OnAccent:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.onAccent.default,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.onAccent.hover,
        },
      };

    case MDSButtonVariant.ContrastAccent:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.contrastAccent.default,
        color: theme.colors.text.primary,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.contrastAccent.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.primary,
        },
      };
    default:
      return baselineStyle;
  }
};

export const StyledSpinnerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: inherit;
  border-radius: inherit;
`;
